import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import PublicLayout from "@/components/public/PublicLayout";
import RangeCalendar from "@/components/public/RangeCalendar";
import PublicCatalog from "@/components/public/PublicCatalog";
import { longDate, money, pickLang, t } from "@/i18n/booking";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const isoToday = () => new Date().toISOString().slice(0, 10);
const plusDays = (n) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);

function defaultSelection(products) {
  const sel = {};
  const groups = {};
  for (const p of products) if (p.exclusive_group) (groups[p.exclusive_group] ||= []).push(p);
  for (const items of Object.values(groups)) {
    const def = items.find((p) => p.meta?.is_default) || items.find((p) => Number(p.price) === 0);
    if (def) sel[def.id] = 1;
  }
  return sel;
}

export default function VehicleBooking({ embedded = false }) {
  const { slug } = useParams();
  const location = useLocation();
  const lang = pickLang(location.search);
  const params = new URLSearchParams(location.search);
  const [vehicle, setVehicle] = useState(null);
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(new Set());
  const [range, setRange] = useState({ start: params.get("start") || "", end: params.get("end") || "" });
  const [selection, setSelection] = useState({});
  const [promo, setPromo] = useState("");
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "", accept: false });
  const [paying, setPaying] = useState(false);
  // Red de seguridad del widget: si el navegador no deja que el iframe cambie la
  // ventana superior, el cliente pulsa un enlace que sí lo hace (clic propio).
  const [payUrl, setPayUrl] = useState("");
  const [payError, setPayError] = useState("");
  const [notFound, setNotFound] = useState(false);
  const prefix = embedded ? "/embed/reservar" : "/reservar";

  useEffect(() => {
    api.get(`/public/vehicles/${slug}`).then(({ data }) => setVehicle(data)).catch(() => setNotFound(true));
    api.get("/public/catalog").then(({ data }) => {
      setProducts(data.products || []);
      setSelection(defaultSelection(data.products || []));
    }).catch(() => {});
    api.get(`/public/vehicles/${slug}/busy`, { params: { start: isoToday(), end: plusDays(365) } })
      .then(({ data }) => setBusy(new Set(data.busy))).catch(() => {});
  }, [slug]);

  const items = useMemo(() => Object.entries(selection).map(([product_id, qty]) => ({ product_id, qty })), [selection]);

  useEffect(() => {
    setQuote(null);
    setQuoteError("");
    if (!range.start || !range.end) return undefined;
    const h = setTimeout(() => {
      api.post("/public/quote", { vehicle_slug: slug, start: range.start, end: range.end, items, promo_code: promo })
        .then(({ data }) => setQuote(data))
        .catch((err) => setQuoteError(formatError(err)));
    }, 300);
    return () => clearTimeout(h);
  }, [slug, range.start, range.end, items, promo]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const pay = async (e) => {
    e.preventDefault();
    setPayError("");
    if (!form.accept) return setPayError(t(lang, "accept"));
    setPaying(true);
    try {
      const { data } = await api.post("/public/bookings", {
        vehicle_slug: slug, start: range.start, end: range.end, items, promo_code: promo, lang, accept_terms: true,
        customer: { name: form.name, email: form.email, phone: form.phone, notes: form.notes },
      });
      if (!embedded) {
        window.location.href = data.url;
        return;
      }
      try {
        window.top.location.href = data.url;
        setTimeout(() => setPayUrl(data.url), 2500);
      } catch {
        setPayUrl(data.url);
      }
    } catch (err) {
      setPayError(formatError(err));
      setPaying(false);
    }
  };

  if (notFound) {
    return (
      <PublicLayout lang={lang} embedded={embedded}>
        {() => <p className="text-[#5C5C5C]">{t(lang, "no_vehicles")} <Link className="underline" to={`${prefix}?lang=${lang}`}>{t(lang, "back")}</Link></p>}
      </PublicLayout>
    );
  }

  return (
    <PublicLayout lang={lang} embedded={embedded}>
      {({ config }) => vehicle && (
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <Link to={`${prefix}?lang=${lang}`} className="text-sm text-[#5C5C5C] hover:text-marca">← {t(lang, "back")}</Link>
            <h1 className="font-display text-3xl font-bold tracking-tight text-[#1A1A1A]" data-testid="vb-title">{vehicle.name}</h1>
            {vehicle.photos?.length > 0 && (
              <div className="flex gap-2 overflow-x-auto">
                {vehicle.photos.map((p) => <img key={p} src={p} alt="" className="h-48 rounded-lg object-cover" />)}
              </div>
            )}
            <p className="text-[#5C5C5C] whitespace-pre-line">{vehicle.description?.[lang] || vehicle.description?.es || ""}</p>
            <section className="bg-white border border-[#DCD8D0] rounded-xl p-5">
              <h2 className="font-display text-lg font-semibold mb-3">{t(lang, "step_dates")}</h2>
              <RangeCalendar lang={lang} busy={busy} start={range.start} end={range.end} onChange={setRange} minDate={isoToday()} />
              <p className="mt-3 text-sm text-[#5C5C5C]" data-testid="vb-range">
                {range.start ? `${longDate(lang, range.start)} → ${range.end ? longDate(lang, range.end) : t(lang, "select_end")}` : t(lang, "select_dates")}
              </p>
            </section>
            <section className="bg-white border border-[#DCD8D0] rounded-xl p-5">
              <h2 className="font-display text-lg font-semibold mb-3">{t(lang, "step_extras")}</h2>
              <PublicCatalog lang={lang} products={products} value={selection} onChange={setSelection}
                vehicleKm={vehicle.km_included_per_day} />
              <div className="mt-4 flex items-center gap-2">
                <Input placeholder={t(lang, "promo_placeholder")} value={promo} onChange={(e) => setPromo(e.target.value.toUpperCase())} className="w-40" data-testid="vb-promo" />
                <span className="text-xs text-[#8A8A8A]">{t(lang, "promo_label")}</span>
              </div>
            </section>
          </div>

          <aside className="space-y-4">
            <section className="bg-white border border-[#DCD8D0] rounded-xl p-5 lg:sticky lg:top-4" data-testid="vb-summary">
              <h2 className="font-display text-lg font-semibold mb-3">{t(lang, "summary")}</h2>
              {!range.end && <p className="text-sm text-[#5C5C5C]">{t(lang, "choose_dates")}</p>}
              {quoteError && <p className="text-sm text-acento" data-testid="vb-quote-error">{quoteError}</p>}
              {quote && (
                <dl className="text-sm space-y-1">
                  <div className="flex justify-between"><dt>{t(lang, "base", { nights: `${quote.nights} ${quote.nights === 1 ? t(lang, "night") : t(lang, "nights")}` })}</dt><dd>{money(lang, quote.base_total)}</dd></div>
                  {quote.promo && <div className="flex justify-between text-marca"><dt>{t(lang, "promo_line", { code: quote.promo.code, pct: quote.promo.discount_pct })}</dt><dd>−{money(lang, quote.discount_total)}</dd></div>}
                  {quote.items.filter((i) => Number(i.subtotal) > 0).map((i) => (
                    <div key={i.product_id} className="flex justify-between"><dt>{i.name}{i.qty > 1 ? ` ×${i.qty}` : ""}</dt><dd>{money(lang, i.subtotal)}</dd></div>
                  ))}
                  <div className="flex justify-between font-semibold border-t border-[#EAE7E0] pt-2"><dt>{t(lang, "total")}</dt><dd data-testid="vb-total">{money(lang, quote.total)}</dd></div>
                  <div className="flex justify-between"><dt>{t(lang, "down_payment", { pct: Number(quote.signal_percent) })}</dt><dd data-testid="vb-signal">{money(lang, quote.signal_amount)}</dd></div>
                  <div className="flex justify-between text-[#5C5C5C]"><dt>{t(lang, "remaining")}</dt><dd>{money(lang, quote.balance_amount)}</dd></div>
                  <p className="text-xs text-[#8A8A8A] pt-2" data-testid="vb-deposit-note">
                    {t(lang, "deposit_note", { amount: money(lang, quote.deposit_amount), days: config?.deposit_hold_max_days ?? 6 })}
                  </p>
                </dl>
              )}
            </section>

            {quote && (
              <form onSubmit={pay} className="bg-white border border-[#DCD8D0] rounded-xl p-5 space-y-3" data-testid="vb-form">
                <h2 className="font-display text-lg font-semibold">{t(lang, "step_details")}</h2>
                <Input required placeholder={t(lang, "name")} value={form.name} onChange={set("name")} data-testid="vb-name" />
                <Input required type="email" placeholder={t(lang, "email")} value={form.email} onChange={set("email")} data-testid="vb-email" />
                <Input required placeholder={t(lang, "phone")} value={form.phone} onChange={set("phone")} data-testid="vb-phone" />
                <Textarea placeholder={t(lang, "notes")} value={form.notes} onChange={set("notes")} />
                <label className="flex items-start gap-2 text-sm">
                  <input type="checkbox" checked={form.accept} onChange={set("accept")} data-testid="vb-accept" />
                  <span>
                    {t(lang, "accept")} ·{" "}
                    <a href={`/condiciones?lang=${lang}`} target="_blank" rel="noopener noreferrer" className="underline" data-testid="vb-terms-link">
                      {t(lang, "terms_link")}
                    </a>
                  </span>
                </label>
                {/* La privacidad se informa, no se acepta: va aparte de la casilla (RGPD, art. 13). */}
                <p className="text-xs text-[#8A8A8A]" data-testid="vb-privacy-notice">
                  {t(lang, "privacy_notice", { company: config?.brand?.legal_name || config?.company_name || "" })}{" "}
                  <a href={`/privacidad?lang=${lang}`} target="_blank" rel="noopener noreferrer" className="underline" data-testid="vb-privacy-link">
                    {t(lang, "privacy_link")}
                  </a>
                </p>
                {payError && <p className="text-sm text-acento" data-testid="vb-pay-error">{payError}</p>}
                {config?.payments_ready === false ? (
                  <p className="rounded-md border border-[#E89B33]/40 bg-[#E89B33]/10 p-3 text-sm text-[#7A4E0E]" data-testid="vb-no-payments">
                    {t(lang, "payments_unavailable")}
                  </p>
                ) : (
                  <Button type="submit" disabled={paying} className="w-full bg-acento hover:bg-acento-hover text-white" data-testid="vb-pay">
                    {paying ? t(lang, "paying") : t(lang, "pay", { amount: money(lang, quote.signal_amount) })}
                  </Button>
                )}
                {payUrl && (
                  <a href={payUrl} target="_top" className="block text-center rounded-md border border-acento text-acento px-4 py-2 text-sm"
                    data-testid="vb-pay-fallback">{t(lang, "pay_continue")}</a>
                )}
                <p className="text-xs text-[#8A8A8A]">{t(lang, "secure")}</p>
              </form>
            )}
          </aside>
        </div>
      )}
    </PublicLayout>
  );
}
